import { ReactNode, MouseEvent, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';

import styles from './Sidebar.module.scss';
import { LawInterfacesEnum } from '../enums/LawInterfacesEnum';
import { IRootState } from '../interfaces/IRootState';
import { scrollToBlock } from '../common/scrollToBlock';
import { KnmLateFieldsLinks } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsLinks';

interface IMenuItem {
  route: string;
  title: ReactNode;
  warning?: ReactNode;
  versionAvailable?: LawInterfacesEnum[];
  renderError?: (className?: string) => ReactNode;
}

interface ISidebarProps {
  type?: 'knm-menu' | 'control-panel';
  menuItems: IMenuItem[];
  locationHash?: string;
  itemsIsAnchors?: boolean;
  announceHeight?: number;
  useClickableWarning?: boolean;
}

const SidebarWithoutStore = ({
  type,
  menuItems,
  locationHash,
  itemsIsAnchors,
  announceHeight,
  useClickableWarning = false,
}: ISidebarProps) => {
  const location = useLocation();

  const renderWarning = (warningContent: ReactNode) => {
    if (useClickableWarning) {
      return <KnmLateFieldsLinks content={<div className={styles.WarningTooltipContent}>{warningContent}</div>} />;
    }

    return (
      <Tooltip
        overlayClassName={styles.WarningTooltip}
        placement="right"
        content={<div className={styles.WarningTooltipContent}>{warningContent}</div>}
      >
        <span className={styles.WarningIcon} />
      </Tooltip>
    );
  };

  const handleAnchorLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const anchorElementId = e.currentTarget.hash.replace('#', '');

    scrollToBlock(anchorElementId);
  }, []);

  const getHashFromRouteString = useCallback((value: string) => {
    const split = value.split('#');

    return `#${split?.[split.length - 1]}`;
  }, []);

  return (
    <aside
      className={styles.Wrapper}
      data-type={type}
      style={announceHeight ? { top: `${announceHeight + 50}px` } : undefined}
    >
      <ul className={styles.List}>
        {menuItems.map(item => {
          const linkAttributes = {
            ...((item.route === location.pathname ||
              (locationHash && getHashFromRouteString(item.route) === locationHash)) && {
              'data-active': true,
            }),
          };

          return (
            <li key={item.route} className={styles.Item} id={item.route}>
              {itemsIsAnchors ? (
                <div className={styles.RowItem}>
                  {item.renderError && item.renderError(styles.ErrorIcon)}
                  <a className={styles.Link} href={item.route} onClick={handleAnchorLinkClick} {...linkAttributes}>
                    {item.title}
                  </a>
                  {item.warning && renderWarning(item.warning)}
                </div>
              ) : (
                <Link className={styles.Link} to={item.route} {...linkAttributes}>
                  {item.title}
                  {item.versionAvailable && (
                    <span className={styles.LinkSpan}>
                      {item.versionAvailable.includes(LawInterfacesEnum.FZ_294)
                        ? `Для ЕРП${item.versionAvailable.includes(LawInterfacesEnum.FZ_248) ? ' и ЕРКНМ' : ''}`
                        : 'Для ЕРКНМ'}
                    </span>
                  )}
                  {item.warning && renderWarning(item.warning)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

const mapStateToProps = (state: IRootState) => ({
  announceHeight: state.core.announceHeight,
});

export const Sidebar = connect(mapStateToProps)(SidebarWithoutStore);
